import {createPreparedPrompt, isDirectTurnSurface} from './contracts'
import {submissionIsUnsettled, isSubmissionRecoveryEnabled} from './submission-recovery'
import type {
  EstablishedAhpSession,
  EstablishedAhpSessionBootstrap,
  EstablishedAhpSessionIdentity,
  EstablishedAhpDirectTurnTelemetry,
  AhpSubmissionMetadata,
  DirectTurnTelemetryContext,
  DirectTurnSurface,
  FirstTurnHandoff,
  AhpChatStartRequest,
  StopOutcome,
  SubmissionRecovery,
} from './contracts'

export interface InitialAhpSubmissionRequest extends AhpChatStartRequest {
  readonly origin: FirstTurnHandoff['origin']
  /** Reuse this id only when the caller is deliberately retrying one request. */
  readonly requestId?: string
  readonly metadata?: AhpSubmissionMetadata
  readonly mode?: FirstTurnHandoff['mode']
  readonly approvalMode?: FirstTurnHandoff['approvalMode']
  readonly configureSession?: FirstTurnHandoff['configureSession']
  readonly beforeDispatch?: FirstTurnHandoff['beforeDispatch']
  readonly afterDispatch?: FirstTurnHandoff['afterDispatch']
  readonly preparePrompt?: FirstTurnHandoff['preparePrompt']
  readonly directTurnTelemetryContext?: DirectTurnTelemetryContext | null
  readonly directTurnTelemetrySurface?: DirectTurnSurface
  readonly directTurnTelemetryContextPending?: FirstTurnHandoff['directTurnTelemetryContextPending']
  readonly directTurnTelemetryTiming?: FirstTurnHandoff['directTurnTelemetryTiming']
  readonly onTurnDispatched?: FirstTurnHandoff['onTurnDispatched']
}

export interface EstablishedAhpInitialSubmissionCoordinator {
  readonly recovery?: SubmissionRecovery
  recover?(): Promise<EstablishedAhpSession>
  prepare?(): void
  submit(request: InitialAhpSubmissionRequest): Promise<EstablishedAhpSession>
  cancel?(): StopOutcome
  dispose?(): void
}

export interface ManagedEstablishedAhpInitialSubmissionCoordinator extends EstablishedAhpInitialSubmissionCoordinator {
  activate(): void
  deactivate(): void
  prepare(): void
  cancel(): StopOutcome
  dispose(): void
}

export interface EstablishedAhpInitialSubmissionOptions {
  readonly bootstrap: EstablishedAhpSessionBootstrap
  readonly identity?: EstablishedAhpSessionIdentity
  readonly directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
  /** Reports newly provisioned identity before relay startup and first-turn acceptance. */
  readonly onIdentityProvisioned?: (identity: EstablishedAhpSessionIdentity) => void
}

/**
 * Coordinates the one first-turn path shared by product bootstraps.
 *
 * The coordinator delegates acceptance, lifecycle, and cleanup to the bootstrap.
 * Recovery reconnects the provisioned session and inspects the original attempt,
 * without creating a task or dispatching another prompt.
 */
export function createEstablishedAhpInitialSubmissionCoordinator({
  bootstrap,
  identity,
  directTurnTelemetry = bootstrap.directTurnTelemetry,
  onIdentityProvisioned,
}: EstablishedAhpInitialSubmissionOptions): ManagedEstablishedAhpInitialSubmissionCoordinator {
  let active = true
  let disposed = false
  const prepareRecovery = async (recoveryIdentity: EstablishedAhpSessionIdentity) => {
    try {
      await bootstrap.establish({mode: 'recover', identity: recoveryIdentity})
    } catch {
      // Preparation is speculative; Submit owns user-visible retry and error handling.
    }
  }
  const reportedProvisionedIdentities = new Set<string>()

  const handleIdentityResolved = (nextIdentity: EstablishedAhpSessionIdentity) => {
    const key = [nextIdentity.taskId, nextIdentity.environmentId, nextIdentity.sessionId, nextIdentity.channel].join(
      '\0',
    )
    if (reportedProvisionedIdentities.has(key)) return
    reportedProvisionedIdentities.add(key)
    onIdentityProvisioned?.(nextIdentity)
  }

  return {
    get recovery() {
      return isSubmissionRecoveryEnabled() ? bootstrap.session?.input.recovery : undefined
    },
    recover: async () => {
      if (disposed || !active) throw new Error('AHP initial submission coordinator is unavailable.')
      const session = bootstrap.session
      if (!isSubmissionRecoveryEnabled() || !session?.input.recovery) {
        throw new Error('The provisioned AHP session is unavailable.')
      }
      const attempt = await session.input.recovery.recover()
      if (!attempt || attempt.phase === 'preparing' || submissionIsUnsettled(attempt)) {
        throw new Error('The host has not confirmed this submission.')
      }
      return session
    },
    activate: () => {
      if (!disposed) active = true
    },
    deactivate: () => {
      active = false
    },
    prepare: () => {
      if (!active || disposed || !identity) return
      void prepareRecovery(identity)
    },
    cancel: () => {
      if (!active || disposed) return {accepted: false}
      return bootstrap.cancelPendingFirstTurn()
    },
    submit: async ({
      prompt,
      origin,
      requestId = crypto.randomUUID(),
      metadata,
      mode,
      approvalMode,
      configureSession,
      beforeDispatch,
      afterDispatch,
      preparePrompt,
      directTurnTelemetryContext,
      directTurnTelemetrySurface,
      directTurnTelemetryContextPending,
      directTurnTelemetryTiming,
      onTurnDispatched,
    }) => {
      if (disposed) throw new Error('AHP initial submission coordinator is disposed.')
      if (!active) throw new Error('AHP initial submission coordinator is inactive.')
      const firstTurn: FirstTurnHandoff = {
        requestId,
        prompt: createPreparedPrompt(prompt),
        origin,
        ...(mode ? {mode} : {}),
        ...(approvalMode ? {approvalMode} : {}),
        ...(configureSession ? {configureSession} : {}),
        ...(beforeDispatch ? {beforeDispatch} : {}),
        ...(afterDispatch ? {afterDispatch} : {}),
        ...(preparePrompt ? {preparePrompt} : {}),
        ...(directTurnTelemetryContext !== undefined ? {directTurnTelemetryContext} : {}),
        ...(directTurnTelemetryContextPending ? {directTurnTelemetryContextPending: true} : {}),
        ...(directTurnTelemetryContextPending && isDirectTurnSurface(directTurnTelemetrySurface)
          ? {directTurnTelemetrySurface}
          : {}),
        ...(directTurnTelemetryTiming ? {directTurnTelemetryTiming} : {}),
        ...(onTurnDispatched ? {onTurnDispatched} : {}),
      }
      const provisionedIdentity = bootstrap.provisionedIdentity
      if (directTurnTelemetryContext !== null) {
        const isNewChat = origin === 'new'
        directTurnTelemetry?.beginSubmission({
          requestId,
          turnKind: isNewChat ? 'new_chat' : 'existing_chat',
          startPath: provisionedIdentity || origin === 'recovery' ? 'resume' : 'cold',
          ...(firstTurn.prompt.model?.id ? {modelId: firstTurn.prompt.model.id} : {}),
          ...(isDirectTurnSurface(directTurnTelemetrySurface) ? {surface: directTurnTelemetrySurface} : {}),
          ...(directTurnTelemetryContext ? {context: directTurnTelemetryContext} : {}),
          ...(directTurnTelemetryContextPending ? {contextPending: true} : {}),
          ...(directTurnTelemetryTiming ? {timing: directTurnTelemetryTiming} : {}),
        })
      }
      try {
        return await (provisionedIdentity
          ? bootstrap.establish({mode: 'resume-provision', firstTurn})
          : identity
            ? bootstrap.establish({mode: 'recover', identity, firstTurn})
            : bootstrap.establish({
                mode: 'provision',
                input: {firstTurn, ...(metadata ? {metadata} : {})},
                onIdentityResolved: handleIdentityResolved,
              }))
      } catch (error) {
        const attempt = isSubmissionRecoveryEnabled() ? bootstrap.session?.input.recovery?.getSnapshot() : undefined
        if (directTurnTelemetryContext !== null && (attempt?.requestId !== requestId || !attempt.dispatch)) {
          directTurnTelemetry?.preDispatchError(requestId)
        }
        throw error
      }
    },
    dispose: () => {
      active = false
      disposed = true
    },
  }
}
